/**
 * `03/ CRF/ Record` (12:8288) — `/countries/:cc/crfs/:crfId`.
 *
 * The record composition follows `InquiryRecordPage` (rail header, PanelTabs,
 * 320px properties panel) and the per-action `can()` gating follows
 * `WorkOrderRecordPage`: D5, a gated action is ABSENT, never disabled.
 *
 * ===========================================================================
 * ANNOTATION 12:9130 — THE TESTING TAB IS VISIBLE AT ZERO
 * ===========================================================================
 * The Testing tab renders with a 0-count badge and the empty state "No test
 * rounds yet" — it does not vanish when nothing has been tested. `PanelTabs`
 * already renders a count of 0 (only null/undefined hide the badge), and
 * `counts.test_rounds` is zero-filled by the API ("a 0 is a count, not a gap",
 * `api.ts`). A tab that disappears at zero is the old Work Board defect class:
 * absence of evidence rendered as evidence of absence. `crf-record.test.tsx`
 * pins this.
 *
 * The record payload carries NO per-round rows — rounds and deployments live on
 * this CRF's WORK ORDERS (`GET /work-orders/:id`). So the Testing and
 * Deployments tabs list the work orders and say "open the work order for
 * detail" rather than fabricating a rounds table this response cannot fill.
 *
 * ===========================================================================
 * "DAYS SINCE CREATED", BECAUSE THAT IS WHAT THE NUMBER IS
 * ===========================================================================
 * The third summary card counts whole days from `created_at`. It is NOT
 * labelled "days in development": the substrate records no in-development
 * timestamp, and a mislabelled number is a lie with a tooltip's precision.
 *
 * ===========================================================================
 * MONEY IS RENDERED VERBATIM — THE `WorkOrderRecordPage` RULE
 * ===========================================================================
 * `amount`/`advance_amount` arrive as the figure OR as `'•••'` (the server's
 * masking interceptor decides, against this CRF's country). Nothing here
 * parses, formats or arithmetises them — `Number('•••')` is `NaN`, and a
 * formatter applied to the mask would put "NaN" where a redaction belongs.
 * The mask renders muted, in the summary card and the properties panel alike.
 *
 * ===========================================================================
 * OQ-A6 — "SEND FOR CONFIRMATION" RECORDS STATE; NO MAIL EXISTS
 * ===========================================================================
 * `POST /crfs/:id/send-confirmation` records the send (or a reminder) and
 * nothing more — there is no customer-mail transport in this system, by owner
 * ruling. The success toast says exactly that. A toast claiming "mail sent"
 * would be the UI lying about a transport that is not there (`api.ts` carries
 * the same warning on the wire function).
 *
 * ===========================================================================
 * STAGE COLOURS RIDE EXISTING TOKEN PAIRS
 * ===========================================================================
 * `CRF_STAGES` names abstract tones (violet/amber/blue/green/slate/red); this
 * file maps each onto a measured `DocTone` pair from `domain/quotes.ts`
 * (`docToneClasses` / `DocRail`) — the pairs the contrast suite already pins.
 * No colour is invented here.
 *
 * ===========================================================================
 * PAYMENT STATUS — A SEPARATE CARD, NOT A FIELD (Train E, fork 15, owner-ruled)
 * ===========================================================================
 * The owner dropped payment status as a CREATE requirement (R4) and forbade it
 * being folded into a CRF EDIT FORM; in the same sentence he endorsed it as a
 * SEPARATE CARD. So the overview mounts the work pipeline's `PaymentPanel` — the
 * ONE control that reaches `POST /crfs/:id/payment-status` — as its own card,
 * gated on `billing.record` exactly as the work-order record gates it (D5:
 * absent, never disabled; the CURRENT status stays readable for every viewer).
 * There is no CRF edit form on this screen and this card is not one: it moves
 * one column through its own route, its own key, its own event.
 *
 * Why here as well as on the work order: an advance learned after confirmation
 * and before any work is cut has, until now, had no screen to land on — the
 * work-order record does not exist yet, and *"frozen, the workaround is a
 * spreadsheet."* The card promises the status can be moved FROM THIS RECORD;
 * `crf-record.test.tsx` drives it against a one-row server model, and
 * `tests/payment-card.smoke.spec.ts` drives it against the real API and reads
 * the row back — a promise about server behaviour is proven across the boundary,
 * not against a mock.
 *
 * ===========================================================================
 * THE ADVANCE DOOR (Train E · E26, fork 14) — A ROW CONTROL, NOT A FORM FIELD
 * ===========================================================================
 * The advance AMOUNT is the same sentence's other half: `POST /crfs/:id/advance`
 * fills the one blank `crfs.advance_amount` after confirmation and refuses to
 * overwrite. Its ONE control on this screen is `AdvanceRow` in the Commercials
 * group — offered only to `crf.edit`, only off-draft, only while the column is
 * NULL and the record is priced (each condition is a server refusal, cited on
 * the component). The figure renders through the SAME masked `MoneyValue` as
 * `amount` wherever it appears. `crf-record.test.tsx` (claim 9) drives the
 * control against a one-row server model; `tests/advance-record.smoke.spec.ts`
 * drives it against the real API, reads the row back by SQL, and reads the same
 * record as a Developer to see the mask; `crf-advance.contrast.test.ts` measures
 * the new surface in both modes.
 */

import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { Paperclip, Upload } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { Alert, Button, Card, Chip, CountryBadge, SkeletonBlock } from '../../components/ui';
import { apiRequest } from '../../lib/api';
import { cn } from '../../lib/cn';
import type { CountryCode } from '../../domain/countries';
import type { EventRow } from '../../domain/events';
import { daysSince, docToneClasses } from '../../domain/quotes';
import type { DocTone } from '../../domain/quotes';
import { DocRail } from '../quotes/status';
// WT5 — `inquiries/panel` died with the slide-over kill; the tab row and the
// property rows relocated verbatim to `record-furniture` (repoint only).
import { PanelTabs, PropertyGroup, PropertyRow } from '../inquiries/record-furniture';
import { formatDateLong, isMaskedAmount } from '../inquiries/format';
import { InquiryActivityFeed } from '../inquiries/Timeline';
import { useToast } from '../inquiries/toast';
import { formatBytes } from '../work-pipeline/WorkFilesPanel';
import { PaymentPanel } from '../work-pipeline/LifecyclePanels';
import { PAYMENT_LABEL } from '../work-pipeline/payment-status';
import { MaintenancePanel } from './MaintenancePanel';
import { WorkTypeBadge } from './WorkTypeBadge';
import {
  CRF_FILE_ACCEPT,
  CRF_FILE_MAX_BYTES,
  confirmCrf,
  crfKeys,
  downloadCrfFile,
  fetchCrf,
  fetchCrfFiles,
  fetchCrfLedger,
  recordCrfAdvance,
  recordPaymentEntry,
  sendCrfConfirmation,
  setCrfReferral,
  uploadCrfFile,
} from './api';
import type {
  CrfDto,
  CrfFileDto,
  PaymentEntryDto,
  CrfPlatformDto,
  CrfPointDto,
  CrfStackDto,
  CrfWorkOrderDto,
  Paged,
} from './api';
import {
  CRF_STAGES,
  MANUAL_DOC_TYPES,
  MANUAL_DOC_TYPE_LABEL,
  POINT_DECISIONS,
  type ManualDocType,
} from './domain';
import { crfErrorText } from './errors';

type RecordTab = 'overview' | 'files' | 'manuals' | 'testing' | 'deployments' | 'activity';

/** Underscored API statuses read as words. No mapping table: nothing is renamed. */
function humanise(value: string): string {
  return value.replace(/_/g, ' ');
}

/**
 * `CRF_STAGES` tone → the measured token pair that paints it. `violet` rides
 * `stage/confirm` and `red` rides `stage/lost` — the same families the quote
 * and sales-order pills draw from, so one meaning keeps one colour app-wide.
 */
const STAGE_TONE_TO_DOC: Readonly<Record<(typeof CRF_STAGES)[string]['tone'], DocTone>> = {
  violet: 'confirm',
  blue: 'info',
  amber: 'warning',
  green: 'success',
  slate: 'neutral',
  red: 'lost',
};

function stageDocTone(status: string): DocTone {
  const descriptor = CRF_STAGES[status];
  return descriptor === undefined ? 'neutral' : STAGE_TONE_TO_DOC[descriptor.tone];
}

/**
 * The stage pill — `StagePill`'s geometry rendered from the CRF map. A separate
 * component for the same reason `QuoteStatusPill` is: `StagePill`'s prop is
 * `InquiryStage`, and a CRF has no inquiry stage. An unknown status (a later
 * phase's enum member) renders humanised on the neutral chip, never blank.
 */
function CrfStagePill({ status }: { status: string }): React.JSX.Element {
  const descriptor = CRF_STAGES[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium',
        docToneClasses(stageDocTone(status)),
      )}
    >
      <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
      {descriptor?.label ?? humanise(status)}
    </span>
  );
}

/**
 * A money field, VERBATIM (see the file header). Quietened and tooltipped when the
 * server sent the mask; the currency code is appended only to a real figure —
 * "••• INR" would dress a redaction up as an amount.
 *
 * ⚠ THE QUIET TOKEN IS `text/secondary`, AND IT USED TO BE `text/muted` — **2.63:1** on
 * `bg/surface` in light mode, against a 4.5 floor. This one mattered most of the seven
 * sites in the sweep: the summary KPI renders it at `text-h2`, which is 20px/600 and
 * therefore still NORMAL text by WCAG (large = ≥18.66px at ≥700, or ≥24px), so the
 * biggest, most authoritative masked figure in the product was a plain AA failure with no
 * large-text excuse available. `text/secondary` measures 4.64 light / 5.85 dark.
 */
function MoneyValue({
  amount,
  currency,
  testId,
}: {
  amount: string | null;
  currency: string | null;
  testId: string;
}): React.JSX.Element {
  if (amount === null) return <span data-testid={testId}>—</span>;
  const masked = isMaskedAmount(amount);
  return (
    <span
      data-testid={testId}
      className={cn('tnum', masked && 'text-fg-secondary')}
      title={masked ? 'Hidden — this account cannot view amounts in this country' : undefined}
    >
      {amount}
      {masked || currency === null ? '' : ` ${currency}`}
    </span>
  );
}

/* ───────────────────────────────────────────────────────────────── events ── */

const crfEventKeys = {
  forCrf: (cc: string, id: string) => ['events', cc, 'crf', id] as const,
};

/**
 * The country feed filtered to this CRF — the same route and shape as
 * `fetchInquiryEvents`, with `entity=crf`. Country scope is the only gate.
 */
function useCrfEvents(
  countryCode: string,
  crfId: string,
  enabled: boolean,
): UseQueryResult<Paged<EventRow>> {
  return useQuery({
    queryKey: crfEventKeys.forCrf(countryCode, crfId),
    queryFn: ({ signal }) =>
      apiRequest<Paged<EventRow>>(`/countries/${countryCode}/events`, {
        query: { entity: 'crf', entityId: crfId, page: 1, perPage: 25 },
        signal,
      }),
    enabled,
  });
}

/* ───────────────────────────────────────────────────────────────── screen ── */

export function CrfRecordPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { crfId } = useParams<{ crfId: string }>();
  const { can } = useSession();

  const canView = can(countryCode, 'crf.view');
  const id = crfId ?? null;
  const record = useQuery({
    queryKey: crfKeys.record(id ?? ''),
    queryFn: ({ signal }) => fetchCrf(id ?? '', signal),
    enabled: canView && id !== null,
  });

  if (!canView) {
    return (
      <div className="p-6">
        <Alert tone="danger" title="This CRF is not available to this account">
          Opening CRFs in {countryCode} needs the CRF permission.
        </Alert>
      </div>
    );
  }

  if (record.isPending) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <SkeletonBlock className="h-4 w-64" />
        <SkeletonBlock className="h-8 w-80" />
        <SkeletonBlock className="h-64 w-full" />
      </div>
    );
  }

  if (record.isError || record.data === undefined) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="This CRF could not be opened">
          {crfErrorText(record.error)}
        </Alert>
        <Link
          to={`/countries/${countryCode}/crfs`}
          className="w-fit text-body13 text-fg underline underline-offset-2 hover:decoration-2"
        >
          Back to the CRF register
        </Link>
      </div>
    );
  }

  return <CrfRecord key={record.data.id} countryCode={countryCode} crf={record.data} />;
}

function CrfRecord({
  countryCode,
  crf,
}: {
  /** Narrowed, not `string`: `can()` takes a `CountryCode` and this is the gate. */
  countryCode: CountryCode;
  crf: CrfDto;
}): React.JSX.Element {
  const { can } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<RecordTab>('overview');

  const canEdit = can(countryCode, 'crf.edit');
  /*
   * The payment card's write gate — the ROUTE's key (`@RequirePerm('billing.record')`
   * on `POST /crfs/:id/payment-status`), not `crf.edit`. A Coordinator holds `crf.edit`
   * and NOT `billing.record` (role-matrix.json), and this screen must agree with the
   * server about who may move the status; the card is still rendered for them, with the
   * current state and no buttons (D5).
   */
  const canRecordPayment = can(countryCode, 'billing.record');
  const isDraft = crf.status === 'draft';

  const events = useCrfEvents(countryCode, crf.id, tab === 'activity');

  const settle = (): void => {
    void queryClient.invalidateQueries({ queryKey: crfKeys.record(crf.id) });
    void queryClient.invalidateQueries({ queryKey: crfEventKeys.forCrf(countryCode, crf.id) });
  };

  const send = useMutation({
    mutationFn: () => sendCrfConfirmation(crf.id),
    onSuccess: () => {
      settle();
      // OQ-A6, verbatim. "Recorded" is the whole truth: the API wrote
      // `confirmation_sent_at` (or bumped the reminder count) and no mail
      // exists to have gone out. See the file header.
      toast.push({
        text: 'Recorded. No customer mail is sent yet — transport awaits an owner ruling.',
      });
    },
    onError: (error) => toast.push({ text: crfErrorText(error), tone: 'error' }),
  });

  const confirm = useMutation({
    mutationFn: () => confirmCrf(crf.id),
    onSuccess: () => {
      settle();
      toast.push({ text: `${crf.doc_no} confirmed.` });
    },
    onError: (error) => toast.push({ text: crfErrorText(error), tone: 'error' }),
  });

  const busy = send.isPending || confirm.isPending;
  const days = daysSince(crf.created_at);
  const points = crf.points ?? [];
  const workOrders = crf.work_orders ?? [];

  return (
    <div className="flex flex-col gap-4 p-6">
      <header className="relative flex items-stretch">
        {/* The L3 spine, bleeding left exactly as the inquiry and quote rails do —
          * `DocRail` records why in-flow was wrong. */}
        <DocRail tone={stageDocTone(crf.status)} />
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <p className="text-label text-fg-muted">
            <Link to={`/countries/${countryCode}/crfs`} className="hover:underline">
              CRFs
            </Link>
            {' / '}
            <span className="font-mono text-mono">{crf.doc_no}</span>
          </p>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <CountryBadge code={countryCode} />
              <h1 className="truncate text-h1 text-fg">{crf.party_name ?? crf.title}</h1>
              {/*
                ⚠ `only={null}` — BOTH work types are badged HERE, unlike on a register.
                On a list the badge marks the exception, because the neighbouring rows
                supply the contrast; on ONE record there is no neighbour, so an absent
                badge and "this build does not know" look identical. The reader is
                entitled to be told which kind of record they have opened.
              */}
              <WorkTypeBadge workType={crf.work_type} only={null} />
              <CrfStagePill status={crf.status} />
            </div>

            <div className="flex items-center gap-2">
              {/* D5 — the pair below is ABSENT without `crf.edit`, and absent
                * off-draft too (a state gate the stage pill beside the title
                * explains). There is no "Edit CRF": no edit screen exists, and a
                * button navigating nowhere would be worse than none. */}
              {canEdit && isDraft ? (
                <Button variant="ghost" disabled={busy} onClick={() => send.mutate()}>
                  {send.isPending ? 'Recording…' : 'Send for confirmation'}
                </Button>
              ) : null}
              {canEdit && isDraft ? (
                <Button disabled={busy} onClick={() => confirm.mutate()}>
                  {confirm.isPending ? 'Confirming…' : 'Confirm CRF'}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <PanelTabs<RecordTab>
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'overview', label: 'Overview' },
          // `counts` is zero-filled by the server; `?? null` covers only a
          // response without the enrichment, where hiding the badge is honest
          // and inventing a 0 would not be.
          { id: 'files', label: 'Files', count: crf.counts?.files ?? null },
          // The manuals badge counts a SUBSET of the files badge, not a disjoint
          // set — a manual is a CRF file that carries a type (migration 55). The
          // two are not meant to sum.
          { id: 'manuals', label: 'Manuals', count: crf.counts?.manuals ?? null },
          { id: 'testing', label: 'Testing', count: crf.counts?.test_rounds ?? null },
          { id: 'deployments', label: 'Deployments', count: crf.counts?.deployments ?? null },
          { id: 'activity', label: 'Activity', count: events.data?.total ?? null },
        ]}
      />

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-5">
          {tab === 'overview' ? (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Card className="gap-1.5">
                  <p className="text-th uppercase text-fg-muted">Quoted value</p>
                  <p className="text-h2 text-fg">
                    <MoneyValue
                      amount={crf.amount}
                      currency={crf.currency}
                      testId="crf-quoted-value"
                    />
                  </p>
                </Card>
                <Card className="gap-1.5">
                  <p className="text-th uppercase text-fg-muted">Advance</p>
                  <p className="text-h2 text-fg">
                    <MoneyValue
                      amount={crf.advance_amount}
                      currency={crf.currency}
                      testId="crf-advance-value"
                    />
                  </p>
                </Card>
                <Card className="gap-1.5">
                  <p className="text-th uppercase text-fg-muted">Pending</p>
                  <p className="text-h2 text-fg">
                    {/*
                      Migration 64: amount − received over the ledger, derived
                      server-side. PB-4: null = UNRESOLVED (no established amount)
                      and renders the em-dash with its reason — never 0.00.
                    */}
                    {crf.pending_amount === null && crf.amount === null ? (
                      <span
                        data-testid="crf-pending-value"
                        title="Unresolved — this CRF has no established amount, so there is no arithmetic to run (PB-4)."
                      >
                        —
                      </span>
                    ) : (
                      <MoneyValue
                        amount={crf.pending_amount}
                        currency={crf.currency}
                        testId="crf-pending-value"
                      />
                    )}
                  </p>
                </Card>
                <Card className="gap-1.5">
                  {/* NOT "days in development" — see the file header. */}
                  <p className="text-th uppercase text-fg-muted">Days since created</p>
                  <p className="tnum text-h2 text-fg">{days === null ? '—' : days}</p>
                </Card>
              </div>

              {/*
                v22-E3 — THE SPLIT, SHOWN AND NEVER TRACKED. Rendered only when the
                server derived one (a no-split country's record simply has no member).
                The amounts ride MoneyValue so a masked figure renders as the mask,
                never as "••• USD" dressed as an amount; the sentence carries the
                TRIGGER — due on delivery — and flips to "due since" once a
                deployment exists, because the deploy IS the send (ruling 3).
              */}
              {crf.payment_split === undefined ? null : (
                <Card className="gap-1.5">
                  {/* Card forwards no data-* attrs; the testid rides the heading. */}
                  <p className="text-th uppercase text-fg-muted" data-testid="crf-payment-split">
                    Payment split
                  </p>
                  <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                    <p className="text-body text-fg">
                      Advance {crf.payment_split.advance_pct}% —{' '}
                      <MoneyValue
                        amount={crf.payment_split.advance_amount}
                        currency={crf.currency}
                        testId="crf-split-advance"
                      />
                    </p>
                    <p className="text-body text-fg">
                      Balance {crf.payment_split.balance_pct}% —{' '}
                      <MoneyValue
                        amount={crf.payment_split.balance_amount}
                        currency={crf.currency}
                        testId="crf-split-balance"
                      />
                    </p>
                  </div>
                  <p className="text-label text-fg-secondary">
                    {crf.payment_split.delivered_at === null
                      ? 'The balance falls due on delivery — the deploy is the send.'
                      : `Balance due since delivery on ${new Date(
                          crf.payment_split.delivered_at,
                        ).toLocaleDateString()}.`}
                  </p>
                </Card>
              )}

              <LedgerCard countryCode={countryCode} crf={crf} />

              <ScopeCard platforms={crf.platforms ?? []} />
              {/*
                ⚠ THE STACK CARD IS ABSENT ON A CRF, NOT EMPTY. The server omits `stacks`
                entirely for `work_type='crf'` — "this record has no stack yet" and "the
                question does not arise for this record" are different facts, and only a
                project is built in a stack (D-2b, migration 39's CHECK). A card reading
                "No stacks" on a CRF would invite somebody to go looking for the picker
                the database will refuse them.
              */}
              {crf.stacks === undefined ? null : <StackCard stacks={crf.stacks} />}
              <PointsCard points={points} />
              {/*
                THE PAYMENT CARD (fork 15) — the work pipeline's own panel, mounted here as
                a SEPARATE card (see the file header). Not gated on status: the route has no
                status gate (`CrfLifecycleService.setPaymentStatus`), so an advance on a
                confirmed CRF with no work order is recordable here — the case the owner
                named. `useSetPaymentStatus` invalidates `['crfs']`, so this record refetches
                and the header, the properties row and the toast agree.
              */}
              <PaymentPanel
                crfId={crf.id}
                current={crf.payment_status}
                countryCode={countryCode}
                canRecord={canRecordPayment}
                onToast={(text, tone) => toast.push({ text, tone })}
              />
              {/*
                ⚠ MOUNTED ONLY FOR A PROJECT, AND THE SERVER AGREES: `GET
                /crfs/:id/maintenance` REFUSES on a CRF, including on the read
                (`MaintenanceService.requireProject`). Rendering it unconditionally would
                paint an error panel on every CRF record in the system.
              */}
              {crf.work_type === 'project' ? (
                <MaintenancePanel crfId={crf.id} canEdit={canEdit} />
              ) : null}
            </>
          ) : null}

          {tab === 'files' ? <CrfFilesCard crfId={crf.id} canUpload={canEdit} /> : null}

          {tab === 'manuals' ? <CrfManualsCard crfId={crf.id} canUpload={canEdit} /> : null}

          {tab === 'testing' ? (
            <LifecycleTab
              title="Testing"
              count={crf.counts?.test_rounds}
              noun="test round"
              emptyLine="No test rounds yet."
              homeLine="Test rounds live on this CRF's work orders."
              detailLine="Open the work order for round detail."
              workOrders={workOrders}
              countryCode={countryCode}
            />
          ) : null}

          {tab === 'deployments' ? (
            <LifecycleTab
              title="Deployments"
              count={crf.counts?.deployments}
              noun="deployment"
              emptyLine="No deployments yet."
              homeLine="Deployments live on this CRF's work orders."
              detailLine="Open the work order for deployment detail."
              workOrders={workOrders}
              countryCode={countryCode}
            />
          ) : null}

          {tab === 'activity' ? (
            <section className="flex flex-col gap-3">
              {events.isPending ? <SkeletonBlock className="h-32 w-full" /> : null}
              {events.isError ? (
                <Alert tone="warning">The activity trail could not be loaded.</Alert>
              ) : null}
              {events.data === undefined ? null : (
                <InquiryActivityFeed events={events.data.data} />
              )}
            </section>
          ) : null}
        </div>

        {/* The 320px properties panel — `Record/PropertiesPanel`'s composition. */}
        <aside className="flex w-full shrink-0 flex-col gap-5 rounded-md border border-line bg-surface p-5 lg:w-[320px]">
          <PropertyGroup label="Customer">
            {/* Text, not a link — WRITTEN when no party screen was mounted and a link
              * would have 404d. The party record page exists now
              * (feat/party-referral-screen, `parties/:partyId`), so linking this name
              * via `crf.party_id` is a live option; it is FLAGGED rather than folded
              * into that cluster, because this screen's tests pin this row and a
              * wiring change here is not a referral-write concern. */}
            <PropertyRow name="Customer">{crf.party_name ?? '—'}</PropertyRow>
            {/*
              * "REFERRED BY" LIVES IN THE CUSTOMER GROUP, NOT COMMERCIALS. It is a fact
              * about who brought the work — the legacy party master's `RefBy`, kept on the
              * party by owner ruling and stated per document here — not a figure, not a
              * payment state, and it never arrives masked. `crf-record.test.tsx` pins the
              * group it is in AND the group it is not in.
              */}
            <ReferredByRow crf={crf} canEdit={canEdit} onSaved={settle} />
          </PropertyGroup>

          <PropertyGroup label="Commercials">
            <PropertyRow name="Quoted">
              <MoneyValue amount={crf.amount} currency={crf.currency} testId="crf-prop-quoted" />
            </PropertyRow>
            {/* The value AND (post-confirmation, blank, priced, `crf.edit`) the ONE
              * write control that reaches `POST /crfs/:id/advance` — see `AdvanceRow`. */}
            <AdvanceRow crf={crf} canEdit={canEdit} onSaved={settle} />
            {/* The SAME word the payment card prints — one screen, one vocabulary. This
              * row is the read-only mirror; the card is the control. */}
            <PropertyRow name="Payment">
              <span data-testid="crf-prop-payment">
                {crf.payment_status === null ? 'Not set' : PAYMENT_LABEL[crf.payment_status]}
              </span>
            </PropertyRow>
          </PropertyGroup>

          {/* Absent entirely when never sent — a "Sent on: —" block would read
            * as a data gap where the truth is "nothing has happened yet". */}
          {crf.confirmation_sent_at === null ? null : (
            <PropertyGroup label="Confirmation">
              <PropertyRow name="Sent on">{formatDateLong(crf.confirmation_sent_at)}</PropertyRow>
              <PropertyRow name="Reminders">
                {crf.confirmation_reminders}{' '}
                {crf.confirmation_reminders === 1 ? 'reminder' : 'reminders'} sent
              </PropertyRow>
            </PropertyGroup>
          )}

          <PropertyGroup label="System">
            <PropertyRow name="CRF ID" mono>
              {crf.doc_no}
            </PropertyRow>
            <PropertyRow name="Created">{formatDateLong(crf.created_at)}</PropertyRow>
            <PropertyRow name="Financial year">{crf.fy_label}</PropertyRow>
          </PropertyGroup>
        </aside>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── referred by ── */

/**
 * The "Referred by" property row — the value, and (for `crf.edit`) the ONE write control
 * that reaches `POST /crfs/:id/referral`, the E22 door.
 *
 * ===========================================================================
 * WHY THIS IS A CONTROL ON THE RECORD AND NOT A FIELD IN AN EDIT FORM
 * ===========================================================================
 * The draft PATCH refuses a confirmed CRF (409), and the owner ruled that a commercial fact
 * learned AFTER confirmation is still recordable — *"frozen, the workaround is a
 * spreadsheet."* So the write goes through the status-free single-column route rather than
 * `updateCrf`, and it is offered on EVERY status: there is no `isDraft` in this component,
 * on purpose. The state gate that hides Confirm/Send above does not apply — the server has
 * no status gate on this route (`CrfsService.setReferral`, measured: zero `status` reads),
 * and a control the server would honour but the screen hid would be the UI inventing a
 * rule.
 *
 * ===========================================================================
 * D5 — ABSENT WITHOUT `crf.edit`, NEVER DISABLED
 * ===========================================================================
 * The VALUE renders for every viewer (the gate removes actions, never facts). The Change /
 * Set control renders ONLY for a seat holding `crf.edit` in this country — the same key the
 * route requires — and is otherwise absent from the DOM: not disabled, not tooltipped. The
 * Coordinator holds `crf.edit` already, so the ruling is served without touching the role
 * matrix (fork 22).
 *
 * ===========================================================================
 * THE WIRE
 * ===========================================================================
 * `{ referral: 'Shah & Co' }` SETS; a blank field sends `{ referral: null }` and CLEARS —
 * the key is mandatory on the route (`{}` is a 400), so the two statements are spelled out
 * rather than left to omission. The response is the WHOLE record as the server now holds it
 * (normalised: trimmed, blank → NULL, and NOTHING re-cased — the `direct` → `Direct` fold
 * this line used to name was struck by the owner on 2026-08-18), and it goes straight into the record cache —
 * the value on screen is the value stored the moment the form closes; the record refetch
 * that `onSaved` also triggers merely agrees, and the activity feed picks up the new row.
 */
function ReferredByRow({
  crf,
  canEdit,
  onSaved,
}: {
  crf: CrfDto;
  canEdit: boolean;
  onSaved: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  const current = crf.referral ?? null;

  const save = useMutation({
    mutationFn: () => {
      const trimmed = value.trim();
      return setCrfReferral(crf.id, { referral: trimmed === '' ? null : trimmed });
    },
    onSuccess: (record) => {
      // The response IS the record (the route returns `CrfsService.get`, the same view
      // `GET /crfs/:id` returns) — put it in the cache as-is so the row shows the stored
      // value the instant the form closes, with no flash of the old one; `onSaved` then
      // invalidates the record (a refetch that agrees) and the activity feed, which now
      // carries one `crf.referral_set` row.
      queryClient.setQueryData(crfKeys.record(crf.id), record);
      setEditing(false);
      onSaved();
      toast.push({
        text:
          record.referral === null || record.referral === undefined
            ? 'Referred by cleared.'
            : `Referred by recorded: ${record.referral}.`,
      });
    },
    onError: (error) => toast.push({ text: crfErrorText(error), tone: 'error' }),
  });

  if (editing) {
    return (
      <form
        data-testid="crf-referral-form"
        className="flex flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <div className="flex items-start gap-2">
          <label htmlFor="crf-referral-input" className="w-[104px] shrink-0 pt-1.5 text-label text-fg-muted">
            Referred by
          </label>
          <input
            id="crf-referral-input"
            type="text"
            value={value}
            maxLength={200}
            autoFocus
            placeholder="A person, a firm, a partner — or Direct"
            onChange={(event) => setValue(event.target.value)}
            className="h-8 min-w-0 flex-1 rounded-sm border border-line bg-surface px-2 text-body13 text-fg placeholder:text-fg-muted"
          />
        </div>
        <div className="flex items-center gap-3 pl-[112px]">
          <button
            type="submit"
            disabled={save.isPending}
            className="inline-flex h-7 items-center rounded-sm bg-indigo px-2.5 text-label font-medium text-fg-inverse hover:bg-indigo/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-label text-fg-secondary hover:text-fg"
          >
            Cancel
          </button>
          <span className="text-label text-fg-muted">Leave it blank to clear.</span>
        </div>
      </form>
    );
  }

  return (
    <PropertyRow name="Referred by">
      <span className="flex items-baseline gap-2">
        {current === null ? (
          <span className="text-fg-muted" data-testid="crf-prop-referral">
            Not recorded
          </span>
        ) : (
          <span data-testid="crf-prop-referral">{current}</span>
        )}
        {canEdit ? (
          <button
            type="button"
            onClick={() => {
              setValue(current ?? '');
              setEditing(true);
            }}
            className="text-label font-medium text-indigo hover:underline"
          >
            {current === null ? 'Set' : 'Change'}
          </button>
        ) : null}
      </span>
    </PropertyRow>
  );
}

/* ─────────────────────────────────────────────────────────── the advance ── */

/**
 * The "Advance" property row — the value (masked or not, the server's call), and the ONE
 * write control that reaches `POST /crfs/:id/advance`, the E26 door (Train E, owner
 * fork 14: *"an advance learned AFTER confirmation IS recordable: a named single-column
 * route that REFUSES TO OVERWRITE. Frozen, the workaround is a spreadsheet."*).
 *
 * ===========================================================================
 * WHEN THE CONTROL IS OFFERED — FOUR CONDITIONS, EACH ONE A SERVER FACT
 * ===========================================================================
 *   `crf.edit`                     — the route's own key (`@RequirePerm('crf.edit')`,
 *                                    crfs.controller.ts). D5: ABSENT without it, never
 *                                    disabled. NOT `billing.record` — an advance AMOUNT
 *                                    is a commercial fact the coordinator types; payment
 *                                    STATUS keeps its own key on the card above.
 *   `advance_amount === null`      — FILL-A-BLANK ONLY. A recorded advance is refused 409
 *                                    through this door (never overwritten, never cleared),
 *                                    so a row already carrying one offers no control. A
 *                                    masked figure arrives `'•••'` — non-null — so a seat
 *                                    that cannot read the figure is not offered a write
 *                                    against it either; only a REAL blank is null on the
 *                                    wire (the interceptor masks values, not absences —
 *                                    amounts-masking.interceptor.ts:330).
 *   `amount !== null`              — an advance against an unpriced record is a 400 with
 *                                    the service's existing sentence
 *                                    (`assertAdvanceCoherent`); the control must not offer
 *                                    a doomed request (the quote builder dead-end rule).
 *   `status !== 'draft'`           — THE ONE SCREEN-SIDE STATE RULE, AND IT IS DELIBERATE.
 *                                    The route itself has NO status gate (measured:
 *                                    advance-capture.e2e-spec.ts drives it at `closed`,
 *                                    still 200), but the door's own 409 sentence assigns a
 *                                    draft's figure to the draft: *"on a draft the figure
 *                                    is edited with the draft; after confirmation it
 *                                    stands as recorded."* A draft's advance is typed at
 *                                    create (`CrfCreatePage`, R4: captured, never
 *                                    required); this door exists for the money that
 *                                    arrives AFTER confirmation. Unlike the referral row
 *                                    above — offered on every status because no other
 *                                    screen writes a referral — hiding here follows the
 *                                    door's documented split, per the build order.
 *
 * ===========================================================================
 * WHAT SUCCESS AND FAILURE SAY
 * ===========================================================================
 * The response is the WHOLE record (the route returns `CrfsService.get`) and goes straight
 * into the record cache, so the row, the summary card and the payment panel read one fact.
 * The success toast names the DOCUMENT and not the figure — the `crf.advance_recorded`
 * family rule (the figure stays off the feed; the row is where it lives, behind
 * `amounts.view`). A refusal surfaces the SERVER's sentence through `crfErrorText` (the
 * 409's wording says exactly where a draft's figure is edited) and the form stays open —
 * nothing was written, and the row keeps saying so.
 *
 * ===========================================================================
 * ⚠ TOKENS — THE NEW SURFACE IS AA IN BOTH MODES, WHICH MEANS TWO DELIBERATE
 * DEPARTURES FROM THE REFERRAL FORM IT OTHERWISE COPIES (crf-advance.contrast.test.ts)
 * ===========================================================================
 *   · The Record control is `text-fg` underlined (17.04 light / 15.11 dark), NOT the
 *     referral row's `text-indigo` — `accent/indigo` on `bg/surface` MEASURES 4.22 light /
 *     3.90 dark, under the 4.5 floor for 12px text. That pre-existing pair is FILED, not
 *     copied (the manuals register's `text-fg-muted` precedent).
 *   · Save wears the WT1/D1a authored fill trio (`bg-indigo-strong`, 5.37 → 5.90 → 6.38
 *     with white), not the referral form's retired `bg-indigo hover:bg-indigo/90` fade
 *     (4.22 rest, 3.59 hover in light). The input rim is `border-rim` (≡ text/secondary,
 *     4.64/5.85), the Combobox trigger's authored control rim. Hints and the label wear
 *     `text-fg-secondary`; the local refusal wears the mode-invariant
 *     `text-danger-fg` on `bg-danger-bg` badge pair (4.52).
 */

/** DECIMAL(14,2)'s shape — `CrfCreatePage`'s courtesy check, same stance: the server's
 * `assertDecimalString` is the authority, this catches it before a round trip. */
const ADVANCE_PATTERN = /^\d{1,12}(\.\d{1,2})?$/;
const ADVANCE_FORMAT_MESSAGE = 'Digits with at most two decimals, e.g. 18000 or 999.50';

function AdvanceRow({
  crf,
  canEdit,
  onSaved,
}: {
  crf: CrfDto;
  canEdit: boolean;
  onSaved: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [localRefusal, setLocalRefusal] = useState<string | null>(null);

  const offer =
    canEdit &&
    crf.status !== 'draft' &&
    crf.advance_amount === null &&
    crf.amount !== null;

  const save = useMutation({
    mutationFn: (advanceAmount: string) =>
      recordCrfAdvance(crf.id, { advance_amount: advanceAmount }),
    onSuccess: (record) => {
      // The response IS the record — into the cache as-is, so the row and the summary
      // card show the stored figure (or the mask) the instant the form closes; `onSaved`
      // then invalidates the record and the activity feed, which now carries one
      // `crf.advance_recorded` row.
      queryClient.setQueryData(crfKeys.record(crf.id), record);
      setEditing(false);
      onSaved();
      // The document, not the figure — the feed's family rule, kept by the toast too.
      toast.push({ text: `Advance recorded on ${crf.doc_no}.` });
    },
    onError: (error) => toast.push({ text: crfErrorText(error), tone: 'error' }),
  });

  if (editing) {
    return (
      <form
        data-testid="crf-advance-form"
        className="flex flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = value.trim();
          if (trimmed === '') {
            setLocalRefusal('Type the advance received before saving.');
            return;
          }
          if (!ADVANCE_PATTERN.test(trimmed)) {
            setLocalRefusal(ADVANCE_FORMAT_MESSAGE);
            return;
          }
          setLocalRefusal(null);
          save.mutate(trimmed);
        }}
      >
        <div className="flex items-start gap-2">
          <label
            htmlFor="crf-advance-input"
            className="w-[104px] shrink-0 pt-1.5 text-label text-fg-secondary"
          >
            Advance
          </label>
          <input
            id="crf-advance-input"
            type="text"
            inputMode="decimal"
            value={value}
            maxLength={20}
            autoFocus
            placeholder="e.g. 18000 or 999.50"
            onChange={(event) => setValue(event.target.value)}
            className="h-8 min-w-0 flex-1 rounded-sm border border-rim bg-surface px-2 text-body13 text-fg placeholder:text-fg-secondary"
          />
          {/* The record's ONE currency — the route takes none; the figure rides this. */}
          {crf.currency === null ? null : (
            <span className="shrink-0 pt-1.5 text-label text-fg-secondary">{crf.currency}</span>
          )}
        </div>
        {localRefusal === null ? null : (
          <p className="ml-[112px] w-fit rounded-sm bg-danger-bg px-2 py-1 text-label text-danger-fg">
            {localRefusal}
          </p>
        )}
        <div className="flex items-center gap-3 pl-[112px]">
          <button
            type="submit"
            disabled={save.isPending}
            className="inline-flex h-7 items-center rounded-sm bg-indigo-strong px-2.5 text-label font-medium text-fg-inverse hover:bg-indigo-strong-hover active:bg-indigo-strong-active disabled:cursor-not-allowed disabled:opacity-40"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setLocalRefusal(null);
            }}
            className="text-label text-fg-secondary hover:text-fg"
          >
            Cancel
          </button>
          {/* Fill-a-blank, said before the write: after Save this figure stands. */}
          <span className="text-label text-fg-secondary">
            Recorded once — this door never overwrites.
          </span>
        </div>
      </form>
    );
  }

  return (
    <PropertyRow name="Advance">
      <span className="flex items-baseline gap-2">
        <MoneyValue
          amount={crf.advance_amount}
          currency={crf.currency}
          testId="crf-prop-advance"
        />
        {offer ? (
          <button
            type="button"
            onClick={() => {
              setValue('');
              setLocalRefusal(null);
              setEditing(true);
            }}
            className="text-label font-medium text-fg underline underline-offset-2 hover:decoration-2"
          >
            Record
          </button>
        ) : null}
      </span>
    </PropertyRow>
  );
}

/* ────────────────────────────────────────────────────────── overview cards ── */

/**
 * The record's scope summary.
 *
 * ⚠ THE NAME IS PRINTED AS THE SERVER SENT IT. There is no `moduleLabel` lookup
 * any more: under the Q8 ruling the vocabulary is a table, the server owns the
 * words, and a client-side label map would be the mirror this build deleted. It
 * also means a value the owner has since RETIRED still reads correctly here —
 * retire means "cannot be newly attached", not "vanishes from the record".
 */
/* ─────────────────────────────────────────────── the payments ledger card ── */

/**
 * The four kinds, in the order the picker offers them.
 *
 * ⚠ A LIST, NOT A `Record<Kind, string>` LABEL MAP — and the reason is a guard,
 * recorded here so nobody "tidies" it back. `opening_balance` is a REGISTERED
 * MONEY NAME (`MONEY_FIELDS`, parked in `REGISTERED_BEFORE_USE` because no
 * response carries a money value under it), and the registry's staleness scan
 * reads `name:` anywhere in the web source as "this money name now ships".
 * A label map keyed by the kinds would put `opening_balance:` in key position
 * and trip it — a TRUE hit for the regex and a FALSE one for its purpose: here
 * the string is a VOCABULARY VALUE, the sibling of `currency` and
 * `payment_status`, and the money on this wire is `amount` (which the
 * interceptor masks). Promoting the entry to MONEY_ON_THE_WIRE to quiet the
 * scan would have made the table say something untrue, so the money-named KEY
 * is gone instead — and the labels stop being four hand-typed duplicates of
 * the vocabulary they name.
 */
const PAYMENT_KINDS = ['payment', 'refund', 'adjustment', 'opening_balance'] as const;
/**
 * Compile-time exhaustiveness, in a TYPE rather than a value (an unread const is
 * an eslint error): a fifth kind absent from the list above makes this alias
 * `never`, and the annotation below stops compiling.
 */
type UnlistedKind = Exclude<PaymentEntryDto['kind'], (typeof PAYMENT_KINDS)[number]>;
export type PaymentKindsAreTotal = UnlistedKind extends never ? true : never;

/** Sentence-case the wire vocabulary: `opening_balance` → `Opening balance`. */
function kindLabel(kind: PaymentEntryDto['kind']): string {
  const words = kind.replace(/_/g, ' ');
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
}

/**
 * THE LEDGER CARD (migration 64, v24 Phase B — spec §4-B4). The entries list +
 * the received/pending line, and — for a `payments.record` holder — an
 * add-entry form that offers ONLY representable requests: the four kinds, a
 * positive decimal amount, the CRF's OWN currency (shown, not chosen — PB-2 is
 * the server's law and the form does not pretend otherwise), a date, and the
 * optional method/invoice/note. `source` has no control: provenance is the
 * server's fact. A note is demanded up front when the kind is `adjustment`,
 * mirroring the DDL rather than discovering it as a 400.
 *
 * RA-4 shows up as an ABSENCE: no edit control, no delete control, on any row,
 * for anyone — corrections are new rows, and the form is the only door.
 */
function LedgerCard({
  countryCode,
  crf,
}: {
  countryCode: ReturnType<typeof useCountryCode>;
  crf: CrfDto;
}): React.JSX.Element | null {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const canRecord = can(countryCode, 'payments.record');
  const ledgerKey = ['crf-ledger', countryCode, crf.id] as const;
  const ledger = useQuery({
    queryKey: ledgerKey,
    queryFn: ({ signal }) => fetchCrfLedger(countryCode, crf.id, signal),
  });

  const [kind, setKind] = useState<PaymentEntryDto['kind']>('payment');
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState('');
  const [method, setMethod] = useState('');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: () =>
      recordPaymentEntry(countryCode, crf.id, {
        kind,
        amount,
        currency: ledger.data?.currency ?? crf.currency ?? '',
        paid_on: paidOn,
        ...(method === '' ? {} : { method: method as Exclude<PaymentEntryDto['method'], null> }),
        ...(invoiceRef.trim() === '' ? {} : { invoice_ref: invoiceRef.trim() }),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      }),
    onSuccess: () => {
      setAmount('');
      setPaidOn('');
      setMethod('');
      setInvoiceRef('');
      setNote('');
      setFormError(null);
      void queryClient.invalidateQueries({ queryKey: ledgerKey });
      // The record's own pending KPI derives from the same ledger.
      void queryClient.invalidateQueries({ queryKey: crfKeys.record(crf.id) });
    },
    onError: (error: unknown) => {
      setFormError(error instanceof Error ? error.message : 'The entry was refused.');
    },
  });

  if (ledger.isLoading) {
    return (
      <Card className="gap-2">
        <SkeletonBlock className="h-16" />
      </Card>
    );
  }
  if (ledger.data === undefined) return null;
  const data = ledger.data;

  return (
    <Card className="gap-3">
      <p className="text-th uppercase text-fg-muted" data-testid="crf-ledger-card">
        Payments ledger
      </p>
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <p className="text-body text-fg">
          Received{' '}
          <MoneyValue amount={data.received_amount} currency={data.currency} testId="crf-ledger-received" />
        </p>
        <p className="text-body text-fg">
          Pending{' '}
          {data.pending_amount === null ? (
            <span
              data-testid="crf-ledger-pending"
              title="Unresolved — this CRF has no established amount (PB-4)."
            >
              —
            </span>
          ) : (
            <MoneyValue amount={data.pending_amount} currency={data.currency} testId="crf-ledger-pending" />
          )}
        </p>
      </div>

      {data.entries.length === 0 ? (
        <p className="text-body13 text-fg-secondary">No entries yet — the ledger starts empty.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line" data-testid="crf-ledger-entries">
          {data.entries.map((entry) => (
            <li key={entry.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-0.5 py-1.5">
              <span className="w-32 text-body13 text-fg">{kindLabel(entry.kind)}</span>
              <span className="text-body13">
                <MoneyValue
                  amount={entry.amount}
                  currency={entry.currency}
                  testId={`crf-ledger-amount-${entry.id}`}
                />
              </span>
              <span className="text-label text-fg-secondary">{entry.paid_on}</span>
              {entry.method === null ? null : (
                <span className="text-label text-fg-secondary">{entry.method}</span>
              )}
              {entry.invoice_ref === null ? null : (
                <span className="text-label text-fg-secondary">{entry.invoice_ref}</span>
              )}
              {entry.source === 'legacy' ? <Chip>legacy</Chip> : null}
              {entry.note === null ? null : (
                <span className="basis-full text-label text-fg-muted">{entry.note}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {canRecord ? (
        <form
          className="flex flex-wrap items-end gap-2 border-t border-line pt-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (kind === 'adjustment' && note.trim() === '') {
              setFormError('An adjustment corrects receipts downward and needs a note saying why.');
              return;
            }
            record.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-label text-fg-secondary">
            Kind
            <select
              className="rounded-sm border border-line bg-surface px-2 py-1.5 text-body13 text-fg"
              value={kind}
              onChange={(event) => setKind(event.target.value as PaymentEntryDto['kind'])}
              data-testid="crf-ledger-kind"
            >
              {PAYMENT_KINDS.map((value) => (
                <option key={value} value={value}>
                  {kindLabel(value)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-label text-fg-secondary">
            Amount ({data.currency})
            <input
              className="tnum w-32 rounded-sm border border-line bg-surface px-2 py-1.5 text-body13 text-fg"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              inputMode="decimal"
              placeholder="0.00"
              required
              data-testid="crf-ledger-amount-input"
            />
          </label>
          <label className="flex flex-col gap-1 text-label text-fg-secondary">
            Paid on
            <input
              type="date"
              className="rounded-sm border border-line bg-surface px-2 py-1.5 text-body13 text-fg"
              value={paidOn}
              onChange={(event) => setPaidOn(event.target.value)}
              required
              data-testid="crf-ledger-paidon"
            />
          </label>
          <label className="flex flex-col gap-1 text-label text-fg-secondary">
            Method
            <select
              className="rounded-sm border border-line bg-surface px-2 py-1.5 text-body13 text-fg"
              value={method}
              onChange={(event) => setMethod(event.target.value)}
            >
              <option value="">—</option>
              {['bank', 'cash', 'cheque', 'upi', 'card', 'other'].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-label text-fg-secondary">
            Invoice ref
            <input
              className="w-28 rounded-sm border border-line bg-surface px-2 py-1.5 text-body13 text-fg"
              value={invoiceRef}
              onChange={(event) => setInvoiceRef(event.target.value)}
              maxLength={40}
            />
          </label>
          <label className="flex min-w-40 flex-1 flex-col gap-1 text-label text-fg-secondary">
            Note{kind === 'adjustment' ? ' (required for an adjustment)' : ''}
            <input
              className="rounded-sm border border-line bg-surface px-2 py-1.5 text-body13 text-fg"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={300}
              data-testid="crf-ledger-note"
            />
          </label>
          <Button type="submit" disabled={record.isPending} data-testid="crf-ledger-submit">
            Record entry
          </Button>
          {formError === null ? null : (
            <p className="basis-full text-label text-danger" role="alert">
              {formError}
            </p>
          )}
        </form>
      ) : null}
    </Card>
  );
}

function ScopeCard({
  platforms,
}: {
  platforms: readonly CrfPlatformDto[];
}): React.JSX.Element {
  return (
    <Card title="Scope summary">
      {platforms.length === 0 ? (
        <p className="text-body13 text-fg-muted">Nothing was recorded on this CRF.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {platforms.map((platform) => (
            <li
              key={platform.platform_id}
              className="flex items-baseline gap-2 text-body13 text-fg"
            >
              <span aria-hidden="true" className="size-1 shrink-0 rounded-full bg-fg-muted" />
              <span>
                {platform.name}
                {platform.detail === null || platform.detail === '' ? null : (
                  <span className="text-fg-muted"> — {platform.detail}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * WHAT THE PROJECT IS BUILT IN — `crf_stacks` (migration 39, owner decision D-2b).
 *
 * ⚠ THE WORDS ARE THE SERVER'S, exactly as the platform card's are. `stacks` is a table
 * the owner edits without a release, so a client-side label map here would be the mirror
 * the Q8 ruling deleted from this feature — and a value the owner has since RETIRED still
 * reads correctly, because retire means "cannot be newly attached", not "vanishes from
 * the record".
 *
 * ⚠ AND THERE IS NO PER-STACK DETAIL, unlike a platform. That is D-2b in the wire: a
 * platform attach carries free text ("Invoice — GST layout"); a stack attach carries none,
 * because "built in Flutter" has nothing to qualify.
 */
function StackCard({ stacks }: { stacks: readonly CrfStackDto[] }): React.JSX.Element {
  return (
    <Card title="Built in">
      {stacks.length === 0 ? (
        /*
         * ⚠ REACHABLE ONLY ON A DRAFT, AND THE SENTENCE SAYS SO. `CrfsService.confirm`
         * refuses to move a project out of draft without at least one stack (D-3), so a
         * confirmed project always has one. Saying "not chosen yet" rather than "none"
         * tells the operator this is a step outstanding, not a property of the record.
         */
        <p className="text-body13 text-fg-muted">No stack has been chosen yet.</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {stacks.map((stack) => (
            <li
              key={stack.stack_id}
              className="inline-flex items-center rounded-pill bg-subtle px-2.5 py-1 text-label text-fg ring-1 ring-line"
            >
              {stack.name}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * `pending` is the unset state, not a decision (`POINT_DECISIONS` deliberately
 * excludes it), so its chip is the neutral treatment while the three decided
 * states carry their `status/*` pair.
 */
const POINT_PILL_CLASSES: Readonly<Record<CrfPointDto['confirmation'], string>> = {
  pending: 'bg-subtle text-fg ring-1 ring-line',
  confirmed: 'bg-ok-bg text-ok-fg',
  not_confirmed: 'bg-danger-bg text-danger-fg',
  on_hold: 'bg-warn-bg text-warn-fg',
};

function pointLabel(confirmation: CrfPointDto['confirmation']): string {
  return POINT_DECISIONS.find((decision) => decision.key === confirmation)?.label ?? 'Pending';
}

function PointsCard({ points }: { points: readonly CrfPointDto[] }): React.JSX.Element {
  const confirmed = points.filter((point) => point.confirmation === 'confirmed').length;
  return (
    <Card
      title={
        <div className="flex items-center gap-2.5">
          <h2 className="text-h3 text-fg">Confirmation points</h2>
          <Chip>
            {confirmed} of {points.length} confirmed
          </Chip>
        </div>
      }
      action={
        // Visible to EVERY viewer: the entry page gates its own edits, and
        // hiding the read path would hide the facts along with the action.
        <Link to="confirmation" className="text-body13 text-indigo hover:underline">
          Open confirmation entry
        </Link>
      }
    >
      {points.length === 0 ? (
        <p className="text-body13 text-fg-muted">
          No confirmation points have been recorded on this CRF yet.
        </p>
      ) : (
        <table className="w-full text-left text-body13">
          <thead>
            <tr className="border-b border-line text-label text-fg-muted">
              <th scope="col" className="w-10 py-1.5 font-medium">
                SR
              </th>
              <th scope="col" className="py-1.5 font-medium">
                Point
              </th>
              <th scope="col" className="py-1.5 font-medium">
                Status
              </th>
              <th scope="col" className="py-1.5 font-medium">
                Remark
              </th>
            </tr>
          </thead>
          <tbody>
            {points.map((point) => (
              <tr key={point.point_no} className="border-b border-line align-top last:border-0">
                <td className="tnum py-2 text-fg-muted">{point.point_no}</td>
                <td className="py-2 text-fg">{point.description}</td>
                <td className="py-2">
                  <span
                    className={cn(
                      'inline-flex items-center whitespace-nowrap rounded-pill px-2 py-0.5 text-label font-medium',
                      POINT_PILL_CLASSES[point.confirmation],
                    )}
                  >
                    {pointLabel(point.confirmation)}
                  </span>
                </td>
                <td className="py-2 text-fg-muted">
                  {point.remark === null || point.remark === '' ? '—' : point.remark}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/* ─────────────────────────────────────────────── testing / deployments tabs ── */

/** `WorkOrderRecordPage`'s vocabulary, so the same type reads the same on both. */
const WORK_TYPE_LABEL: Readonly<Record<string, string>> = {
  new: 'New',
  correction: 'Correction',
  dl_file: 'DL file',
  ready_deploy: 'Ready to deploy',
};

/**
 * One body for Testing and Deployments: the count (a fact this response DOES
 * carry) plus the work orders where the per-round/per-deployment rows actually
 * live. See the file header — no rows are fabricated here.
 */
function LifecycleTab({
  title,
  count,
  noun,
  emptyLine,
  homeLine,
  detailLine,
  workOrders,
  countryCode,
}: {
  title: string;
  count: number | undefined;
  noun: string;
  emptyLine: string;
  homeLine: string;
  detailLine: string;
  workOrders: readonly CrfWorkOrderDto[];
  countryCode: CountryCode;
}): React.JSX.Element {
  return (
    <Card title={title}>
      {count === undefined ? null : count === 0 ? (
        <p className="text-body13 text-fg">{emptyLine}</p>
      ) : (
        <p className="text-body13 text-fg">
          {count === 1
            ? `1 ${noun} is recorded on this CRF's work orders.`
            : `${String(count)} ${noun}s are recorded on this CRF's work orders.`}
        </p>
      )}

      {workOrders.length === 0 ? (
        <p className="text-label text-fg-muted">{homeLine}</p>
      ) : (
        <>
          <p className="text-label text-fg-muted">{detailLine}</p>
          <table className="w-full text-left text-body13">
            <thead>
              <tr className="border-b border-line text-label text-fg-muted">
                <th scope="col" className="py-1.5 font-medium">
                  Work order
                </th>
                <th scope="col" className="py-1.5 font-medium">
                  Type
                </th>
                <th scope="col" className="py-1.5 font-medium">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {workOrders.map((workOrder) => (
                <tr key={workOrder.id} className="border-b border-line last:border-0">
                  <td className="py-2">
                    <Link
                      to={`/countries/${countryCode}/work/${workOrder.id}`}
                      className="text-fg underline underline-offset-2 hover:decoration-2"
                    >
                      Work order {workOrder.id}
                    </Link>
                  </td>
                  <td className="py-2 text-fg-muted">
                    {WORK_TYPE_LABEL[workOrder.type] ?? workOrder.type}
                  </td>
                  <td className="py-2 text-fg-muted">{humanise(workOrder.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Card>
  );
}

/* ─────────────────────────────────────────────────────────────── files tab ── */

/**
 * Files — the `WorkFilesPanel` idioms on the CRF routes: upload behind
 * `crf.edit` (D5: absent, not disabled), a local size refusal BEFORE the
 * request, and download through the authenticated client (a plain `<a href>`
 * carries no Authorization header and would 401 — the quote-PDF precedent).
 */
function CrfFilesCard({
  crfId,
  canUpload,
}: {
  crfId: string;
  canUpload: boolean;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const files = useQuery({
    queryKey: crfKeys.files(crfId),
    queryFn: ({ signal }) => fetchCrfFiles(crfId, signal),
  });
  const upload = useMutation({
    mutationFn: (file: File) => uploadCrfFile(crfId, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: crfKeys.files(crfId) });
      // The record carries the Files tab badge (`counts.files`) — refetch it
      // so the count does not contradict the list below it.
      void queryClient.invalidateQueries({ queryKey: crfKeys.record(crfId) });
    },
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const [localRefusal, setLocalRefusal] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const send = (file: File | undefined): void => {
    if (file === undefined) return;
    setLocalRefusal(null);
    if (file.size > CRF_FILE_MAX_BYTES) {
      // Refused here rather than sent-and-refused; the server still enforces it.
      setLocalRefusal(
        `${file.name} is ${formatBytes(file.size)}. The limit is ${formatBytes(CRF_FILE_MAX_BYTES)}, so it was not sent.`,
      );
      return;
    }
    upload.mutate(file);
  };

  const download = async (file: CrfFileDto): Promise<void> => {
    setDownloadError(null);
    setBusyId(file.id);
    try {
      const result = await downloadCrfFile(crfId, file.id);
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = result.filename ?? file.file_name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      // RULE 11: a download that silently does nothing teaches the operator
      // the file is gone.
      setDownloadError(crfErrorText(error));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card title="Files">
      {canUpload ? (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              disabled={upload.isPending}
              onClick={() => inputRef.current?.click()}
            >
              <Upload aria-hidden="true" className="size-4" />
              {upload.isPending ? 'Uploading…' : 'Upload a file'}
            </Button>
            <p className="text-label text-fg-muted">
              PDF, ZIP, PNG, JPG or TDL — up to {formatBytes(CRF_FILE_MAX_BYTES)}.
            </p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={CRF_FILE_ACCEPT}
            className="hidden"
            onChange={(event) => {
              send(event.target.files?.[0]);
              // Cleared so choosing the SAME file twice fires `change` again —
              // a retry after a failed upload must not be silently ignored.
              event.target.value = '';
            }}
          />
          {localRefusal === null ? null : (
            <Alert tone="warning" title="That file was not sent">
              {localRefusal}
            </Alert>
          )}
          {upload.isError ? (
            <Alert tone="danger" title="The upload was refused">
              {crfErrorText(upload.error)}
            </Alert>
          ) : null}
        </div>
      ) : null}

      {files.isPending ? (
        <p className="text-body13 text-fg-muted">Loading the attached files…</p>
      ) : files.isError ? (
        // NOT an empty table: "could not be read" is not "nothing is attached".
        <Alert tone="danger" title="The file list could not be read">
          {crfErrorText(files.error)} Nothing below is a count of what is attached.
        </Alert>
      ) : (files.data ?? []).length === 0 ? (
        <p className="text-body13 text-fg-muted">
          Nothing attached yet.
          {canUpload ? '' : ' Attaching files is not part of your access.'}
        </p>
      ) : (
        <table className="w-full text-left text-body13">
          <thead>
            <tr className="border-b border-line text-label text-fg-muted">
              <th scope="col" className="py-1.5 font-medium">
                File
              </th>
              <th scope="col" className="py-1.5 text-right font-medium">
                Size
              </th>
              <th scope="col" className="py-1.5 text-right font-medium">
                Attached
              </th>
            </tr>
          </thead>
          <tbody>
            {(files.data ?? []).map((file) => (
              <tr key={file.id} className="border-b border-line last:border-0">
                <td className="py-2 text-fg">
                  <button
                    type="button"
                    disabled={busyId === file.id}
                    onClick={() => {
                      void download(file);
                    }}
                    className="flex min-w-0 items-center gap-1.5 text-left underline underline-offset-2 hover:decoration-2 disabled:opacity-60"
                  >
                    <Paperclip aria-hidden="true" className="size-3.5 shrink-0 text-fg-muted" />
                    <span className="truncate">{file.file_name}</span>
                    {busyId === file.id ? (
                      <span className="text-label text-fg-muted">downloading…</span>
                    ) : null}
                  </button>
                </td>
                <td className="tnum py-2 text-right text-fg-muted">
                  {formatBytes(file.size_bytes)}
                </td>
                <td className="tnum py-2 text-right text-fg-muted">
                  {formatDateLong(file.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {downloadError === null ? null : (
        <Alert tone="danger" title="That file could not be downloaded">
          {downloadError}
        </Alert>
      )}
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────── manuals tab ── */

/**
 * THE MANUALS REGISTER — the legacy module, per CRF (v20 D1, migration 55).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THIS IS A REGISTER OF
 * ═══════════════════════════════════════════════════════════════════════════
 * The legacy verdict (`4d3127f:docs/REPORT-LATEST.md` §5) names TWO hardcoded
 * types — "Screen Short" and "Final Manual" — and rules "No new table". So a
 * manual here is a CRF attachment carrying `doc_type`, and this screen is the
 * SAME query the Files tab reads (`crfKeys.files`), grouped by that column. One
 * cache entry serves both, which is why the badge, the Files list and this
 * register cannot disagree about what is attached: there is only one answer to
 * disagree with.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * FOLLOWS `CrfFilesCard` LINE FOR LINE, INCLUDING ITS THREE-STATE LADDER
 * ═══════════════════════════════════════════════════════════════════════════
 *   pending → a loading line, never an empty register;
 *   error   → a DANGER `Alert` that says in words that a read failure is not an
 *             empty register (rule 11: silence teaches the operator a false fact);
 *   empty   → one line, with the permission-aware clause so a reader who cannot
 *             upload learns why there is no button rather than assuming a bug.
 *
 * Upload is the SAME mutation as the Files tab's, one path, one set of byte
 * rules — the type rides the query string (see `uploadCrfFile`). The local size
 * refusal fires before the request, and download goes through the authenticated
 * client because a plain `<a href>` carries no Authorization header.
 *
 * ⚠ NO PENDING QUEUE, AND THAT IS DELIBERATE. The verdict also mentions "a
 * derived pending queue" in the legacy. The legacy PHP is not in this repository
 * or its history (measured: zero `.php` paths in `git log --all --name-only`), so
 * the queue's ELIGIBILITY RULE — which records owe a manual, from which stage,
 * and whether both types or only the Final Manual are required — cannot be
 * recovered, only invented. This screen therefore states what IS filed and never
 * what is OWED: a heading with nothing under it reads "Nothing filed under this
 * heading yet", which is an observation, not an obligation. Building the queue is
 * an owner decision, and it is flagged in the report rather than guessed at here.
 *
 * ⚠ NO COLOUR IS INVENTED — AND ONE IS DELIBERATELY NOT COPIED.
 * Every class below is an existing token pair (`text-fg`, `text-fg-secondary`,
 * `border-line`, the shared `Card`, `Alert` and `Button`), pinned per mode by
 * `crf-manuals.contrast.test.ts`. But this register uses `text-fg-secondary`
 * where `CrfFilesCard` above uses `text-fg-muted`, and the divergence is
 * MEASURED, not stylistic:
 *
 *     --crf-text-muted     #9aa0aa on --crf-bg-surface #ffffff  =  2.63:1  ✗ AA
 *     --crf-text-secondary #6e7580 on --crf-bg-surface #ffffff  =  4.64:1  ✓ AA
 *                          #9a9a9a on #1f1f1f (dark)            =  5.83:1  ✓ AA
 *
 * `text-fg-muted` is MODE-INVARIANT (`tokens.css:53` — one value for both themes)
 * and fails AA on the light card at 12-13px. Every honest-absence line this screen
 * renders — the loading line, "No manuals filed yet", "Nothing filed under this
 * heading yet" — is exactly the text that must not be the hardest to read, so it
 * does not inherit that pair.
 *
 * ⚠ THAT MAKES THE FILES CARD ABOVE A PRE-EXISTING AA DEFECT, FOUND BY BUILDING
 * THIS ONE. It is FILED FOR THE OWNER, NOT FIXED HERE: `text-fg-muted` is worn by
 * screens far beyond this file, and re-toning them is a design decision with a
 * blast radius, not a unit's side effect.
 */
function CrfManualsCard({
  crfId,
  canUpload,
}: {
  crfId: string;
  canUpload: boolean;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const files = useQuery({
    queryKey: crfKeys.files(crfId),
    queryFn: ({ signal }) => fetchCrfFiles(crfId, signal),
  });
  const upload = useMutation({
    mutationFn: ({ file, docType }: { file: File; docType: ManualDocType }) =>
      uploadCrfFile(crfId, file, docType),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: crfKeys.files(crfId) });
      // The record carries BOTH badges (`counts.files` and `counts.manuals`) —
      // refetch it so neither contradicts the register below.
      void queryClient.invalidateQueries({ queryKey: crfKeys.record(crfId) });
    },
  });
  const screenShortRef = useRef<HTMLInputElement>(null);
  const finalManualRef = useRef<HTMLInputElement>(null);
  const inputRefs: Record<ManualDocType, React.RefObject<HTMLInputElement>> = {
    screen_short: screenShortRef,
    final_manual: finalManualRef,
  };
  const [localRefusal, setLocalRefusal] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const send = (file: File | undefined, docType: ManualDocType): void => {
    if (file === undefined) return;
    setLocalRefusal(null);
    if (file.size > CRF_FILE_MAX_BYTES) {
      // Refused here rather than sent-and-refused; the server still enforces it.
      setLocalRefusal(
        `${file.name} is ${formatBytes(file.size)}. The limit is ${formatBytes(CRF_FILE_MAX_BYTES)}, so it was not sent.`,
      );
      return;
    }
    upload.mutate({ file, docType });
  };

  const download = async (file: CrfFileDto): Promise<void> => {
    setDownloadError(null);
    setBusyId(file.id);
    try {
      const result = await downloadCrfFile(crfId, file.id);
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = result.filename ?? file.file_name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      // RULE 11: a download that silently does nothing teaches the operator the
      // document is gone.
      setDownloadError(crfErrorText(error));
    } finally {
      setBusyId(null);
    }
  };

  // The register is the typed rows of the SAME list the Files tab reads. Server
  // order (newest first) is preserved inside each heading.
  const manuals = (files.data ?? []).filter((file) => file.doc_type !== null);

  return (
    <Card title="Manuals">
      {canUpload ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {MANUAL_DOC_TYPES.map((docType) => (
              <Button
                key={docType}
                variant="ghost"
                disabled={upload.isPending}
                onClick={() => inputRefs[docType].current?.click()}
              >
                <Upload aria-hidden="true" className="size-4" />
                {upload.isPending ? 'Uploading…' : `Attach a ${MANUAL_DOC_TYPE_LABEL[docType]}`}
              </Button>
            ))}
            <p className="text-label text-fg-secondary">
              PDF, ZIP, PNG, JPG or TDL — up to {formatBytes(CRF_FILE_MAX_BYTES)}.
            </p>
          </div>
          {MANUAL_DOC_TYPES.map((docType) => (
            <input
              key={docType}
              ref={inputRefs[docType]}
              type="file"
              aria-label={`Attach a ${MANUAL_DOC_TYPE_LABEL[docType]}`}
              accept={CRF_FILE_ACCEPT}
              className="hidden"
              onChange={(event) => {
                send(event.target.files?.[0], docType);
                // Cleared so choosing the SAME file twice fires `change` again —
                // a retry after a failed upload must not be silently ignored.
                event.target.value = '';
              }}
            />
          ))}
          {localRefusal === null ? null : (
            <Alert tone="warning" title="That file was not sent">
              {localRefusal}
            </Alert>
          )}
          {upload.isError ? (
            <Alert tone="danger" title="The upload was refused">
              {crfErrorText(upload.error)}
            </Alert>
          ) : null}
        </div>
      ) : null}

      {files.isPending ? (
        <p className="text-body13 text-fg-secondary">Loading the document register…</p>
      ) : files.isError ? (
        // NOT an empty register: "could not be read" is not "nothing is filed".
        <Alert tone="danger" title="The document register could not be read">
          {crfErrorText(files.error)} Nothing below is a count of what is filed — this
          is a read failure, not an empty register.
        </Alert>
      ) : manuals.length === 0 ? (
        <p className="text-body13 text-fg-secondary">
          No manuals filed yet.
          {canUpload ? '' : ' Attaching manuals is not part of your access.'}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {MANUAL_DOC_TYPES.map((docType) => {
            const rows = manuals.filter((file) => file.doc_type === docType);
            return (
              <section key={docType} className="flex flex-col gap-1.5">
                <h3 className="text-label font-medium text-fg">
                  {MANUAL_DOC_TYPE_LABEL[docType]}
                </h3>
                {rows.length === 0 ? (
                  // An observation, never an obligation — this register does not
                  // know which records OWE a manual (see the header's note on the
                  // legacy's derived pending queue).
                  <p className="text-body13 text-fg-secondary">
                    Nothing filed under this heading yet.
                  </p>
                ) : (
                  <table className="w-full text-left text-body13">
                    <caption className="sr-only">
                      {MANUAL_DOC_TYPE_LABEL[docType]} documents on this CRF
                    </caption>
                    <thead>
                      <tr className="border-b border-line text-label text-fg-secondary">
                        <th scope="col" className="py-1.5 font-medium">
                          Document
                        </th>
                        <th scope="col" className="py-1.5 text-right font-medium">
                          Size
                        </th>
                        <th scope="col" className="py-1.5 text-right font-medium">
                          Attached
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((file) => (
                        <tr key={file.id} className="border-b border-line last:border-0">
                          <td className="py-2 text-fg">
                            <button
                              type="button"
                              disabled={busyId === file.id}
                              onClick={() => {
                                void download(file);
                              }}
                              className="flex min-w-0 items-center gap-1.5 text-left underline underline-offset-2 hover:decoration-2 disabled:opacity-60"
                            >
                              <Paperclip
                                aria-hidden="true"
                                className="size-3.5 shrink-0 text-fg-secondary"
                              />
                              <span className="truncate">{file.file_name}</span>
                              {busyId === file.id ? (
                                <span className="text-label text-fg-secondary">downloading…</span>
                              ) : null}
                            </button>
                          </td>
                          <td className="tnum py-2 text-right text-fg-secondary">
                            {formatBytes(file.size_bytes)}
                          </td>
                          <td className="tnum py-2 text-right text-fg-secondary">
                            {formatDateLong(file.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            );
          })}
        </div>
      )}

      {downloadError === null ? null : (
        <Alert tone="danger" title="That document could not be downloaded">
          {downloadError}
        </Alert>
      )}
    </Card>
  );
}
